import { useEffect, useReducer, useRef, useState } from 'react';
import './Modifier.style.css';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

/**
 * Modifier Button
 * --
 */
const ModifierButton = ({
  id,
  style,
  onClick,
  icon,
  disabled,
  className,
  ratio,
}) => {
  return (
    <div className="modifier_btn_box">
      <button
        className={`modifier_btn ${className}`}
        style={{
          fontSize: `${ratio * 0.5}px`,
          height: `${ratio}px`,
          width: `${ratio}px`,
          cursor: `${disabled ? 'not-allowed' : 'pointer'}`,
          ...style,
        }}
        id={id}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
      </button>
    </div>
  );
};

/**
 * 숫자 콤마 포맷
 * --
 */
const formatNumber = (num) =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * INCREASE, DECREASE, SET(input) reducer
 * --
 */
const reducer = (state, action) => {
  switch (action.type) {
    case 'INCREASE':
      return { count: state.count + action.step };
    case 'DECREASE':
      return { count: state.count - action.step };
    case 'SET':
      return { count: action.value };
    default:
      return state;
  }
};

/* ===== Layout ===== */
const Modifier = ({
  init = 0, // 초기값
  step = 1, // 한 번에 증가/감소하는 값
  minNum = 0, // 최소값
  maxNum = 9999999, // 최대값
  unit = '개', // 단위명

  increaseIcon = <PlusOutlined />, // + 아이콘
  decreaseIcon = <MinusOutlined />, // - 아이콘

  onIncrease, // Increase 함수
  onDecrease, // Decrease 함수
  onChange, // input onChange
  layoutStyle = {},
  buttonStyle = {},
  inputStyle = {},
  countStyle = {},

  disabled = false,
  readOnly = false,
}) => {
  /* ===== State ===== */
  const [state, dispatch] = useReducer(reducer, {
    count: init,
  });
  const { count } = state;

  const layoutRef = useRef(null);
  const [layoutHeight, setLayoutHeight] = useState(0);

  /* ===== Hook ===== */
  /**
   * layout 높이 -> 비율
   * --
   */
  useEffect(() => {
    setLayoutHeight(layoutRef.current?.offsetHeight);

    dispatch({ type: 'SET', value: init ? init : minNum });
  }, [init, minNum]);

  /* ===== Function ===== */
  const handleChange = (e) => {
    const value = Number(e.target.value.replace(/,/g, ''));
    if (!isNaN(value) && value >= 0 && value <= maxNum) {
      dispatch({ type: 'SET', value });
    }
  };

  const handleIncrease = () => {
    const newValue = count + step;
    if (newValue <= maxNum) {
      dispatch({ type: 'INCREASE', step });
    }
  };

  const handleDecrease = () => {
    const newValue = count - step;
    if (newValue >= minNum) {
      dispatch({ type: 'DECREASE', step });
    }
  };

  /* ===== Render ===== */
  return (
    <div ref={layoutRef} className="modifier_layout" style={layoutStyle}>
      <ModifierButton
        style={buttonStyle}
        id="decrease"
        onClick={onDecrease ? onDecrease : handleDecrease}
        icon={decreaseIcon}
        disabled={count === minNum || disabled}
        className="left"
        ratio={layoutHeight * 0.81}
      />
      <div
        className="modifier_count"
        style={{
          fontSize: `${(layoutHeight * 0.81) / 2}px`,
          ...countStyle,
        }}
      >
        <input
          style={{
            fontSize: `${(layoutHeight * 0.81) / 2}px`,
            width: `${layoutHeight * 0.81 * 1.1}px`,

            ...inputStyle,
          }}
          type="text"
          id={'count'}
          // defaultValue={minNum}
          value={formatNumber(count)}
          placeholder={formatNumber(init)}
          onChange={onChange ? onChange : handleChange}
          readOnly={readOnly}
        />
        <span>{unit}</span>
      </div>
      <ModifierButton
        style={buttonStyle}
        id="increase"
        onClick={onIncrease ? onIncrease : handleIncrease}
        icon={increaseIcon}
        disabled={count === maxNum || disabled}
        className="right"
        ratio={layoutHeight * 0.81}
      />
    </div>
  );
};

export default Modifier;
